import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/landing/Logo";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      const to = location.state?.from?.pathname || "/admin";
      navigate(to, { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream flex flex-col" data-testid="admin-login-page">
      <header className="bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-login-home-link">
            <Logo tone="light" size="sm" />
          </Link>
          <span className="text-xs uppercase tracking-widest text-white/50">Admin</span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-md bg-white p-8 md:p-10 border border-brand-navy/10 rounded-3xl shadow-sm" data-testid="admin-login-form">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-navy text-brand-orange mb-6">
            <Lock size={20} />
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-brand-navy">Admin sign in</h1>
          <p className="mt-2 text-sm text-brand-slate/75">
            Internal access only. View leads and manage enquiries.
          </p>

          <div className="mt-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-brand-navy">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder=""
                data-testid="admin-login-email"
                className="rounded-xl h-11"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-brand-navy">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder=""
                data-testid="admin-login-password"
                className="rounded-xl h-11"
              />
            </div>
          </div>

          {error && (
            <p className="mt-4 text-sm text-red-600" data-testid="admin-login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full bg-brand-orange text-white px-6 py-3 font-semibold hover:bg-brand-orange-hover transition-colors rounded-full disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <Link to="/" className="block mt-6 text-sm text-brand-slate/70 hover:text-brand-orange" data-testid="admin-login-back-link">
            ← Back to landing page
          </Link>
        </form>
      </main>
    </div>
  );
}
